// Adapted from '@atlassian/jira-issue-table/src/common/fields/issue-key';
import React, { memo, type MouseEvent, type KeyboardEvent } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { ff } from '@atlassian/jira-feature-flagging';
import type { issueKey_nativeIssueTable_IssueKeyCell$key } from '@atlassian/jira-relay/src/__generated__/issueKey_nativeIssueTable_IssueKeyCell.graphql';
// eslint-disable-next-line jira/import/no-restricted-paths
import { issueKeySelector } from '../../../../ui/row/styled';
import ErrorCell from '../../error-cell';
import { TableCellFocusBox } from '../../table-cell-focus-box';
import TableCellLink from '../../table-cell-link';

export type Props = {
	fieldRef: issueKey_nativeIssueTable_IssueKeyCell$key;
	/**
	 * Dispatched when a table cell with a link to the issue is selected. This method should return `true` to signal
	 * that the event has been handled, which will prevent default event handling.
	 */
	onIssueLinkSelected?: (issueKey: string) => boolean;
};
export const IssueKeyCell = memo<Props>(({ fieldRef, onIssueLinkSelected }: Props) => {
	const { text: issueKey } = useFragment<issueKey_nativeIssueTable_IssueKeyCell$key>(
		graphql`
			fragment issueKey_nativeIssueTable_IssueKeyCell on JiraSingleLineTextField {
				text
			}
		`,
		fieldRef,
	);

	if (!issueKey) {
		return <ErrorCell cellType="issueKey" reason="Missing required fields" />;
	}

	return ff('nin-resizable-columns_8z4eo') ? (
		<Box paddingBlock="space.050" paddingInline="space.100" xcss={wrapperStyles}>
			<Tooltip content={issueKey}>
				<TableCellLink
					href={`/browse/${issueKey}`}
					onClick={(e: MouseEvent | KeyboardEvent) => {
						// Use default browser event handling when a modifier key is pressed during the click event
						const isModifierKeyUsed = e.ctrlKey || e.shiftKey || e.metaKey;
						const handled =
							!isModifierKeyUsed &&
							onIssueLinkSelected !== undefined &&
							onIssueLinkSelected(issueKey);
						if (handled) {
							e.preventDefault();
						}
					}}
				>
					{issueKey}
				</TableCellLink>
			</Tooltip>
		</Box>
	) : (
		<CellWrapper>
			<Tooltip content={issueKey}>
				<TableCellLink
					href={`/browse/${issueKey}`}
					onClick={(e: MouseEvent | KeyboardEvent) => {
						// Use default browser event handling when a modifier key is pressed during the click event
						const isModifierKeyUsed = e.ctrlKey || e.shiftKey || e.metaKey;
						const handled =
							!isModifierKeyUsed &&
							onIssueLinkSelected !== undefined &&
							onIssueLinkSelected(issueKey);
						if (handled) {
							e.preventDefault();
						}
					}}
					data-component-selector={issueKeySelector}
				>
					{issueKey}
				</TableCellLink>
			</Tooltip>
		</CellWrapper>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellWrapper = styled(TableCellFocusBox)({
	whiteSpace: 'nowrap',
	fontSize: '12px',
	fontWeight: 600,
});

const wrapperStyles = xcss({
	font: token('font.heading.xxsmall'),
	maxWidth: '100%',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});
